import { useState } from "react";
import { Plus, X } from "lucide-react";
import { toast } from "sonner";
import { api, eur, pct } from "@/lib/api";
import { Stat } from "@/components/Badges";
import { Button } from "@/components/ui/button";
import { Field } from "@/components/calc/FoodCostCalc";
import { t } from "@/lib/i18n";

export const PricingCalc = () => {
  const [f, setF] = useState({ dish_cost: 6.5, target_fc_pct: 30, target_margin_pct: 68, vat_pct: 10, fixed_menu_fc_pct: 32 });
  const [courses, setCourses] = useState([4.2, 7.5, 3.0]);
  const [newCourse, setNewCourse] = useState("");
  const [res, setRes] = useState(null);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const calc = async () => {
    try {
      const r = await api.post("/pricing/calc", { dish_cost: +f.dish_cost, target_fc_pct: +f.target_fc_pct, target_margin_pct: f.target_margin_pct ? +f.target_margin_pct : null, vat_pct: +f.vat_pct, fixed_menu_costs: courses, fixed_menu_fc_pct: +f.fixed_menu_fc_pct });
      setRes(r.data);
    } catch { toast.error("Controlla i valori inseriti"); }
  };

  return (
    <div className="grid lg:grid-cols-[380px_1fr] gap-6">
      <div className="surface p-5 space-y-4">
        <div className="eyebrow">{t("Piatto singolo")}</div>
        <div className="grid grid-cols-2 gap-3">
          <Field label={t("Costo materia prima (€)")} id="pr-cost" type="number" step="0.1" value={f.dish_cost} onChange={set("dish_cost")} />
          <Field label={t("Food cost target %")} id="pr-fc" type="number" value={f.target_fc_pct} onChange={set("target_fc_pct")} />
          <Field label={t("Margine target %")} id="pr-margin" type="number" value={f.target_margin_pct} onChange={set("target_margin_pct")} />
          <div className="space-y-1.5">
            <label className="text-xs text-muted-foreground">{t("IVA")}</label>
            <div className="flex gap-1 p-1 bg-background rounded-md border border-border">
              {[10, 22].map((v) => <button key={v} data-testid={`pr-vat-${v}`} onClick={() => setF({ ...f, vat_pct: v })} className={`flex-1 text-xs py-1.5 rounded ${+f.vat_pct === v ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}>{v}%</button>)}
            </div>
          </div>
        </div>
        <div className="eyebrow pt-2">{t("Menù fisso · costo per portata")}</div>
        <div className="flex flex-wrap gap-1.5">
          {courses.map((c, i) => <span key={i} className="num text-xs px-2 py-1 rounded bg-secondary flex items-center gap-1">{eur(c)}<button data-testid={`pr-course-remove-${i}`} onClick={() => setCourses(courses.filter((_, j) => j !== i))}><X size={11} /></button></span>)}
        </div>
        <div className="flex gap-2">
          <Field label="" id="pr-course-input" type="number" step="0.1" placeholder={t("Costo portata")} value={newCourse} onChange={(e) => setNewCourse(e.target.value)} />
          <Button data-testid="pr-course-add" variant="outline" size="icon" className="mt-auto" onClick={() => { if (newCourse) { setCourses([...courses, +newCourse]); setNewCourse(""); } }}><Plus size={14} /></Button>
        </div>
        <Field label={t("Food cost target menù %")} id="pr-menu-fc" type="number" value={f.fixed_menu_fc_pct} onChange={set("fixed_menu_fc_pct")} />
        <Button data-testid="pr-calc-btn" onClick={calc} className="w-full">{t("Calcola prezzi")}</Button>
      </div>

      <div className="space-y-5">
        {res ? (
          <div data-testid="pr-result" className="space-y-4 fade-up">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              <Stat testId="pr-net-fc" label={t("Prezzo netto (da FC)")} value={eur(res.net_by_fc)} hint={t("lordo IVA {v}", { v: eur(res.gross_by_fc) })} accent="#E67E22" />
              <Stat testId="pr-cm" label={t("Margine di contribuzione")} value={eur(res.contribution_margin)} hint={t("per porzione, netto IVA")} />
              {res.net_by_margin != null && <Stat testId="pr-net-margin" label={t("Prezzo netto (da margine)")} value={eur(res.net_by_margin)} hint={t("lordo IVA {v}", { v: eur(res.gross_by_margin) })} />}
              <Stat testId="pr-menu" label={t("Menù fisso · {n} portate", { n: res.menu.courses })} value={eur(res.menu.gross)} hint={t("netto {n} · costo {c}", { n: eur(res.menu.net), c: eur(res.menu.cost) })} />
            </div>
            <div className="surface overflow-hidden">
              <div className="p-4 border-b border-border eyebrow">{t("Simulazioni di prezzo")}</div>
              <table className="w-full text-sm">
                <thead className="text-xs text-muted-foreground"><tr className="border-b border-border"><th className="text-left p-3">{t("Food cost %")}</th><th className="text-right p-3">{t("Prezzo netto")}</th><th className="text-right p-3">{t("Prezzo lordo")}</th><th className="text-right p-3">{t("Margine")}</th></tr></thead>
                <tbody>{res.simulations.map((s) => (
                  <tr key={s.fc_pct} className={`border-b border-border last:border-0 ${s.fc_pct === +f.target_fc_pct ? "bg-primary/10" : ""}`}>
                    <td className="p-3 num">{pct(s.fc_pct)}</td><td className="p-3 text-right num">{eur(s.net)}</td><td className="p-3 text-right num font-semibold">{eur(s.gross)}</td><td className="p-3 text-right num">{eur(s.net - f.dish_cost)}</td>
                  </tr>
                ))}</tbody>
              </table>
            </div>
            <p className="text-xs text-muted-foreground">{t("Il prezzo da costo è il pavimento, non il prezzo finale: confrontalo con il posizionamento, la percezione del valore (naming, descrizione) e i competitor del territorio.")}</p>
          </div>
        ) : <div className="surface p-8 text-center text-sm text-muted-foreground">{t("Inserisci il costo del piatto e calcola.")}</div>}
      </div>
    </div>
  );
};
